// =======================
// detalhesMantenedora.js
// =======================

import React, { useState, useEffect } from "react";
import {
  CssBaseline,
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Tab,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { botaoVoltar } from "../../../estilos/botoes";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import SchoolIcon from "@mui/icons-material/School";
import EmailIcon from "@mui/icons-material/Email";
import ListaAtos from "../../../componentes/ListaAtos";
import { api } from "@api/api";
import { ajustarTamanhoFonteAnalista } from "../../../estilos/global";

// Componente para apresentar cada informação
const Field = ({ label, value, sx = {} }) => (
  <Box
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 0,
      ...sx,
    }}
  >
    <Typography
      sx={{
        color: "text.secondary",
        fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        color: "text.primary",
        fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
        lineHeight: 1.35,
        mt: 0.5,
        overflowWrap: "anywhere",
      }}
    >
      {value || "Não informado"}
    </Typography>
  </Box>
);

// Cartão com ícone e título
const InfoCard = ({ icon, title, children }) => (
  <Box
    sx={{
      p: 2,
      minWidth: 0,
      height: "100%",
      boxSizing: "border-box",
      border: "1px solid #c9def4",
      borderRadius: "6px",
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.75,
        color: "primary.main",
        "& .MuiSvgIcon-root": {
          fontSize: "1.35rem",
        },
      }}
    >
      {icon}

      <Typography
        sx={{
          color: "primary.main",
          fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
    </Box>

    {children}
  </Box>
);

const formatCNPJ = (cnpj) => {
  if (!cnpj) return "";

  const numeros = String(cnpj).replace(/\D/g, "");

  if (numeros.length !== 14) return cnpj;

  return numeros.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5",
  );
};

const formatTelefone = (telefone) => {
  if (!telefone) return "";

  const numeros = String(telefone).replace(/\D/g, "");

  if (numeros.length === 11) {
    return numeros.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  if (numeros.length === 10) {
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return telefone;
};

const formatCEP = (cep) => {
  if (!cep) return "";

  const numeros = String(cep).replace(/\D/g, "");

  if (numeros.length !== 8) return cep;

  return numeros.replace(/(\d{5})(\d{3})/, "$1-$2");
};

export default function Infomantenedora() {
  const [tabValue, setTabValue] = useState("1");
  const { id } = useParams(); // Pega o ID da URL
  const [mantenedoraData, setMantenedoraData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Requisição à API para obter os dados da mantenedora
    const fetchMantenedoraData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(api.maintainerDetails(id), {
          credentials: "include",
        });

        if (response.status === 404) {
          setMantenedoraData(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Erro ao buscar mantenedora: ${response.status}`);
        }

        const data = await response.json();

        setMantenedoraData(data);
      } catch (error) {
        console.error("Erro ao carregar os dados da mantenedora:", error);
        setMantenedoraData(null);
        setError("Não foi possível carregar os dados da mantenedora.");
      } finally {
        setLoading(false);
      }
    };

    fetchMantenedoraData();
  }, [id, retryAttempt]);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return <Typography variant="h6">Carregando...</Typography>;
  }

  // Mensagem para falha de conexão ou erro da API
  if (error) {
    return (
      <Container maxWidth={false} disableGutters sx={{ pt: 3, py: 4 }}>
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setRetryAttempt((valor) => valor + 1)}
                sx={{
                  minWidth: 130,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "6px",
                }}
              >
                Tentar novamente
              </Button>

              <Button
                variant="outlined"
                onClick={handleBackClick}
                startIcon={<ArrowBackRoundedIcon />}
                sx={[botaoVoltar, { mb: 1.5 }]}
              >
                Voltar
              </Button>
            </Box>
          }
          sx={{
            alignItems: "center",
            "& .MuiAlert-message": {
              flex: 1,
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
              mr: 0,
            },
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Não foi possível carregar os dados da mantenedora.
          </Typography>

          <Typography variant="body2">
            Verifique sua conexão e tente novamente.
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (!mantenedoraData) {
    return (
      <Container maxWidth={false} disableGutters sx={{ py: 4 }}>
        <Alert
          severity="warning"
          variant="outlined"
          action={
            <Button
              variant="outlined"
              onClick={handleBackClick}
              startIcon={<ArrowBackRoundedIcon />}
              sx={[botaoVoltar, { mb: 1.5 }]}
            >
              Voltar
            </Button>
          }
          sx={{
            alignItems: "center",
            "& .MuiAlert-message": {
              flex: 1,
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
              mr: 0,
            },
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Mantenedora não encontrada.
          </Typography>

          <Typography variant="body2">
            O registro informado não existe ou não está mais disponível.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <CssBaseline />

      <Container maxWidth={false} disableGutters sx={{ py: 4 }}>
        {/* Cabeçalho externo da página */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            mb: 0.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBackClick}
            startIcon={<ArrowBackRoundedIcon />}
            sx={[botaoVoltar, { mb: 1.5 }]}
          >
            Voltar
          </Button>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              fontSize: { xs: ajustarTamanhoFonteAnalista("1.65rem"), sm: ajustarTamanhoFonteAnalista("1.75rem") },
            }}
          >
            Perfil Mantenedora
          </Typography>
        </Box>

        <TabContext value={tabValue}>
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <TabList
              onChange={handleChangeTab}
              aria-label="Abas do perfil da mantenedora"
              sx={{
                minHeight: 44,
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTabs-flexContainer": {
                  gap: 0.5,
                },
              }}
            >
              <Tab
                label="Informações da Mantenedora"
                value="1"
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: ajustarTamanhoFonteAnalista("0.85rem"), sm: ajustarTamanhoFonteAnalista("1rem") },
                  fontWeight: 500,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderBottom: 0,
                  borderRadius: "6px 6px 0 0",
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                }}
              />

              <Tab
                label="Atos da Mantenedora"
                value="2"
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: ajustarTamanhoFonteAnalista("0.85rem"), sm: ajustarTamanhoFonteAnalista("1rem") },
                  fontWeight: 500,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderBottom: 0,
                  borderRadius: "6px 6px 0 0",
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                }}
              />

              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box component="span">Escolas Mantidas</Box>

                    <Box
                      component="span"
                      sx={{
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "6px",
                        backgroundColor: tabValue === "3" ? "#fff" : "#eaf3fd",
                        color: "primary.main",
                        fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                        fontWeight: 700,
                      }}
                    >
                      {mantenedoraData.escolas?.length || 0}
                    </Box>
                  </Box>
                }
                value="3"
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: ajustarTamanhoFonteAnalista("0.85rem"), sm: ajustarTamanhoFonteAnalista("1rem") },
                  fontWeight: 500,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderBottom: 0,
                  borderRadius: "6px 6px 0 0",
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                }}
              />
            </TabList>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0 8px 8px 8px",
            }}
          >
            <TabPanel value="1" sx={{ p: 0 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "minmax(0, 1.40fr) minmax(0, 0.85fr)",
                  },
                  p: 2,
                  mb: 1.5,
                  border: "1px solid #c9def4",
                  borderRadius: "6px",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    pr: { sm: 2 },
                  }}
                >
                  <Typography
                    sx={{
                      color: "primary.main",
                      fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                      fontWeight: 600,
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    Nome da Mantenedora
                  </Typography>

                  <Typography
                    sx={{
                      color: "text.primary",
                      fontSize: ajustarTamanhoFonteAnalista("1.1rem"),
                      fontWeight: 500,
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {mantenedoraData.nome || "Não informado"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                    mt: { xs: 2, sm: 0 },
                    pt: { xs: 2, sm: 0 },
                    pl: { sm: 2 },
                    borderTop: {
                      xs: "1px solid #d8e3ef",
                      sm: "none",
                    },
                    borderLeft: {
                      xs: "none",
                      sm: "1px solid #d8e3ef",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "primary.main",
                      fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                      fontWeight: 600,
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    CNPJ
                  </Typography>

                  <Typography
                    sx={{
                      color: "text.primary",
                      fontSize: ajustarTamanhoFonteAnalista("1.1rem"),
                      fontWeight: 500,
                      lineHeight: 1.35,
                    }}
                  >
                    {formatCNPJ(mantenedoraData.cnpj) || "Não informado"}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1.35fr 0.85fr",
                  },
                  gap: 1.5,
                  alignItems: "stretch",
                }}
              >
                <InfoCard icon={<LocationOnIcon />} title="Endereço">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(0, 1.4fr) minmax(80px, 0.6fr)",
                    }}
                  >
                    <Field
                      label="Logradouro (Rua/Avenida)"
                      value={mantenedoraData.rua}
                      sx={{
                        px: 0,
                        py: 1,
                        pr: 1.5,
                        borderRight: "1px solid #d8e3ef",
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="Número"
                      value={mantenedoraData.numero}
                      sx={{
                        px: 0,
                        py: 1,
                        pl: 1.5,
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="Bairro"
                      value={mantenedoraData.bairro}
                      sx={{
                        px: 0,
                        py: 1,
                        pr: 1.5,
                        borderRight: "1px solid #d8e3ef",
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="CEP"
                      value={formatCEP(mantenedoraData.cep)}
                      sx={{
                        px: 0,
                        py: 1,
                        pl: 1.5,
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="Município"
                      value={mantenedoraData.cidade}
                      sx={{
                        px: 0,
                        pt: 1.25,
                        pb: 0,
                        pr: 1.5,
                        borderRight: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="Estado (UF)"
                      value={mantenedoraData.estado}
                      sx={{
                        px: 0,
                        pt: 1.25,
                        pb: 0,
                        pl: 1.5,
                      }}
                    />
                  </Box>
                </InfoCard>

                <InfoCard icon={<PhoneIcon />} title="Contatos">
                  <Field
                    label="Telefone"
                    value={formatTelefone(mantenedoraData.telefone)}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: "1px solid #d8e3ef",
                    }}
                  />

                  <Field
                    label="E-mail"
                    value={mantenedoraData.email}
                    sx={{
                      px: 0,
                      pt: 1.25,
                      pb: 0,
                    }}
                  />
                </InfoCard>
              </Box>
            </TabPanel>

            <TabPanel value="2" sx={{ p: 0 }}>
              <ListaAtos />
            </TabPanel>

            <TabPanel value="3" sx={{ p: 0 }}>
              {mantenedoraData.escolas?.length > 0 ? (
                <Box sx={{ display: "grid", gap: 1.25 }}>
                  {mantenedoraData.escolas.map((escolaMantida) => (
                    <Box
                      key={escolaMantida.id}
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        gap: 2,
                        p: 1.5,
                        border: "1px solid #c9def4",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* Ícone da escola */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          borderRadius: "50%",
                          backgroundColor: "#eaf3fd",
                          color: "primary.main",
                        }}
                      >
                        <SchoolIcon />
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(0, 1.5fr) minmax(120px, 0.75fr) minmax(0, 1fr)",
                          },
                          gap: { xs: 1.5, sm: 2 },
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {/* Nome */}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: "primary.main",
                              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            Nome
                          </Typography>

                          <Typography
                            sx={{
                              color: "text.primary",
                              fontSize: ajustarTamanhoFonteAnalista("1.1rem"),
                              fontWeight: 500,
                              lineHeight: 1.35,
                              mt: 0.5,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {escolaMantida.nome || "Não informado"}
                          </Typography>
                        </Box>

                        {/* Cidade */}
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "primary.main",
                            }}
                          >
                            <LocationOnIcon sx={{ fontSize: "1.35rem" }} />

                            <Typography
                              sx={{
                                color: "primary.main",
                                fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                                fontWeight: 600,
                                lineHeight: 1.2,
                              }}
                            >
                              Cidade
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              color: "text.primary",
                              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                              fontWeight: 400,
                              lineHeight: 1.35,
                              mt: 0.5,
                            }}
                          >
                            {escolaMantida.municipio || "Não informado"}
                          </Typography>
                        </Box>

                        {/* E-mail */}
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "primary.main",
                            }}
                          >
                            <EmailIcon sx={{ fontSize: "1.35rem" }} />

                            <Typography
                              sx={{
                                color: "primary.main",
                                fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                                fontWeight: 600,
                                lineHeight: 1.2,
                              }}
                            >
                              E-mail
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              color: "text.primary",
                              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                              fontWeight: 400,
                              lineHeight: 1.35,
                              mt: 0.5,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {escolaMantida.email || "Não informado"}
                          </Typography>
                        </Box>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          navigate(`/perfil-escola/${escolaMantida.id}`)
                        }
                        sx={{
                          minWidth: 100,
                          alignSelf: { xs: "stretch", md: "center" },
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "6px",
                          flexShrink: 0,
                        }}
                      >
                        Ver perfil
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Alert severity="info" variant="outlined">
                  Nenhuma escola vinculada a esta mantenedora.
                </Alert>
              )}
            </TabPanel>
          </Paper>
        </TabContext>
      </Container>
    </motion.div>
  );
}
